/**
 * ProgressFeed — Doserne Home "Feed" section.
 *
 * Facebook-style horizontal-photo timeline that scrolls vertically.
 * Each item is a project card with:
 *   • project name + latest contributor + relative time
 *   • up to 8 recent photos in a mosaic (click → lightbox)
 *   • Chat / Timeline buttons
 *   • latest note preview
 *
 * Photos are lazy-loaded and the whole component uses a single
 * `/api/doserne/progress/feed` call so the initial paint is cheap.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Camera, MessageCircle, Clock, X, MapPin, ChevronLeft, ChevronRight, Trash2, Heart, ThumbsUp } from "lucide-react";
import { timeAgo } from "./ChatRoomView";
import { useDoserneAuth } from "./doserneAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Lightbox({ photos, index, onClose, onPrev, onNext, canDeleteAt, onDelete }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  const p = photos[index];
  const touchX = useRef(null);
  if (!p) return null;
  const canDelete = canDeleteAt ? canDeleteAt(index) : false;
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 50 && photos.length > 1) (dx < 0 ? onNext() : onPrev());
    touchX.current = null;
  };
  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4" onClick={onClose} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-testid="feed-lightbox">
      <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" style={{ color: "#fff" }} aria-label="Lukk">
        <X className="w-5 h-5" />
      </button>
      {canDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete && onDelete(index); }}
          className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-600/90 hover:bg-red-700 text-white text-xs font-black backdrop-blur"
          data-testid="feed-lightbox-delete"
          title="Slett dette bildet"
        >
          <Trash2 className="w-3.5 h-3.5" /> Slett bilde
        </button>
      )}
      {photos.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); onPrev(); }} className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" style={{ color: "#fff" }} aria-label="Forrige">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onNext(); }} className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" style={{ color: "#fff" }} aria-label="Neste">
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}
      <div className="max-w-6xl max-h-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
        <img src={p.url} alt="" className="max-w-full max-h-[85vh] rounded-lg object-contain" />
        <div className="mt-3 text-xs text-slate-300 text-center">
          {p.author_name} · {p.progress_date} · {index + 1}/{photos.length}
        </div>
      </div>
    </div>
  );
}

// Small helper — a lazy `<img>` that hides itself if the URL fails to
// load. Used inside PhotoMosaic to keep broken Cloudinary URLs from
// rendering as browser broken-image placeholders.
function FailSafeImg({ url, onErrorUrl }) {
  return (
    <img
      src={url}
      alt=""
      loading="lazy"
      onError={() => onErrorUrl(url)}
      className="w-full h-full object-cover hover:scale-[1.02] transition-transform"
    />
  );
}

function PhotoMosaic({ photos, onOpen }) {
  // Drop entries with missing or empty URLs and hide any image that
  // fails to load (defensive against Cloudinary being unreachable,
  // signed URLs expiring, or old rows with a null `url`). This is why
  // some feed cards showed 4 broken-image placeholders in production.
  const cleanPhotos = React.useMemo(
    () => (photos || []).filter((p) => p && typeof p.url === "string" && p.url.trim()),
    [photos],
  );
  const [failed, setFailed] = React.useState(() => new Set());
  const onImgError = React.useCallback((url) => setFailed((prev) => {
    const next = new Set(prev);
    next.add(url);
    return next;
  }), []);
  const displayPhotos = cleanPhotos.filter((p) => !failed.has(p.url));

  const count = displayPhotos.length;
  if (count === 0) return null;
  // Layouts: 1 full, 2 side-by-side, 3 (1 big + 2 small), 4 grid, 5+ (2x2 + counter overlay on last)
  if (count === 1) {
    return (
      <button onClick={() => onOpen(0)} className="block w-full aspect-video rounded-xl overflow-hidden bg-slate-100" data-testid="feed-photo-0">
        <FailSafeImg url={displayPhotos[0].url} onErrorUrl={onImgError} />
      </button>
    );
  }
  if (count === 2) {
    return (
      <div className="grid grid-cols-2 gap-1.5">
        {displayPhotos.map((p, i) => (
          <button key={i} onClick={() => onOpen(i)} className="block aspect-square rounded-xl overflow-hidden bg-slate-100" data-testid={`feed-photo-${i}`}>
            <FailSafeImg url={p.url} onErrorUrl={onImgError} />
          </button>
        ))}
      </div>
    );
  }
  if (count === 3) {
    return (
      <div className="grid grid-cols-2 gap-1.5">
        <button onClick={() => onOpen(0)} className="row-span-2 block aspect-square rounded-xl overflow-hidden bg-slate-100" data-testid="feed-photo-0">
          <FailSafeImg url={displayPhotos[0].url} onErrorUrl={onImgError} />
        </button>
        {[1, 2].map((i) => (
          <button key={i} onClick={() => onOpen(i)} className="block aspect-square rounded-xl overflow-hidden bg-slate-100" data-testid={`feed-photo-${i}`}>
            <FailSafeImg url={displayPhotos[i].url} onErrorUrl={onImgError} />
          </button>
        ))}
      </div>
    );
  }
  if (count === 4) {
    return (
      <div className="grid grid-cols-2 gap-1.5">
        {displayPhotos.map((p, i) => (
          <button key={i} onClick={() => onOpen(i)} className="block aspect-square rounded-xl overflow-hidden bg-slate-100" data-testid={`feed-photo-${i}`}>
            <FailSafeImg url={p.url} onErrorUrl={onImgError} />
          </button>
        ))}
      </div>
    );
  }
  // 5+ — show first 4 with "+N" overlay on the 4th thumb when there are more
  const extra = count - 4;
  return (
    <div className="grid grid-cols-2 gap-1.5">
      {displayPhotos.slice(0, 4).map((p, i) => (
        <button
          key={i}
          onClick={() => onOpen(i)}
          className="relative block aspect-square rounded-xl overflow-hidden bg-slate-100"
          data-testid={`feed-photo-${i}`}
        >
          <FailSafeImg url={p.url} onErrorUrl={onImgError} />
          {i === 3 && extra > 0 && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-3xl font-black" style={{ color: "#fff" }}>
              +{extra}
            </div>
          )}
        </button>
      ))}
    </div>
  );
}

function formatDayLabel(dateStr) {
  // Given a YYYY-MM-DD, return a friendly Norwegian label (I dag / I går /
  // dd.mm.yyyy). Falls back to the raw string if parsing fails.
  if (!dateStr) return "";
  const today = new Date();
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, "0");
  const d = String(today.getDate()).padStart(2, "0");
  const todayISO = `${y}-${m}-${d}`;
  if (dateStr === todayISO) return "I dag";
  const yest = new Date(today.getTime() - 86400000);
  const y2 = yest.getFullYear();
  const m2 = String(yest.getMonth() + 1).padStart(2, "0");
  const d2 = String(yest.getDate()).padStart(2, "0");
  const yestISO = `${y2}-${m2}-${d2}`;
  if (dateStr === yestISO) return "I går";
  // dd.mm.yyyy
  const parts = dateStr.split("-");
  if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
  return dateStr;
}

function groupPhotosByDay(photos) {
  // Groups photos by progress_date and returns an array of
  // { date, label, photos: [...] } sorted newest-day-first.
  // Within each day photos are sorted by posted_at ASCENDING (order
  // of posting, oldest→newest) — the user asked "in ordinea postarilor".
  const buckets = new Map();
  for (const p of photos || []) {
    const key = (p.progress_date || (p.posted_at || "").slice(0, 10)) || "";
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(p);
  }
  const days = [];
  for (const [date, list] of buckets.entries()) {
    list.sort((a, b) => (a.posted_at || "").localeCompare(b.posted_at || ""));
    days.push({ date, label: formatDayLabel(date), photos: list });
  }
  days.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  return days;
}

function FeedItem({ item, authHeader, onRemoved, onReacted, onPhotoRemoved }) {
  const { user: me } = useDoserneAuth();
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reacting, setReacting] = useState(false);
  const initials = (item.latest_author_name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  const meEmail = (me?.email || "").toLowerCase();
  const isAdmin = !!me?.is_admin;
  const days = React.useMemo(() => groupPhotosByDay(item.photos || []), [item.photos]);
  // Flat list mirrors the ordering used inside the lightbox so index-based
  // navigation stays coherent when the user jumps day → day.
  const flatPhotos = React.useMemo(
    () => days.flatMap((d) => d.photos.map((p) => ({ ...p, _day: d.date }))),
    [days],
  );
  const canDeletePhotoAt = (idx) => {
    const p = flatPhotos[idx];
    if (!p) return false;
    if (isAdmin) return true;
    return (p.author_email || "").toLowerCase() === meEmail;
  };

  // Feed cards are now emitted per-POST (one card per progress post) — the
  // backend sets `item.project_id = POST_ID` for React-key uniqueness and
  // exposes the REAL project id in `item.real_project_id`. Any navigation
  // that targets the project detail page MUST use the real id, otherwise
  // the router loads a "project" whose id is actually a post → 404 →
  // "Kunne ikke laste prosjekt". Reactions endpoint keys by post id
  // (matches storage) so it keeps using `item.project_id`.
  const realProjectId = item.real_project_id || item.project_id;

  const openChat = () => {
    window.dispatchEvent(new CustomEvent("doserne:open-project-chat", { detail: { project_id: realProjectId } }));
  };

  const react = async (emoji) => {
    if (reacting) return;
    setReacting(true);
    const wasSet = (item.my_reactions || []).includes(emoji);
    // Optimistic update.
    const nextReactions = { ...(item.reactions || {}) };
    const prevMine = (item.my_reactions || []);
    // Remove any previous reaction from this user on this card.
    for (const em of prevMine) {
      if (nextReactions[em]) {
        nextReactions[em] = Math.max(0, nextReactions[em] - 1);
        if (!nextReactions[em]) delete nextReactions[em];
      }
    }
    let nextMine = [];
    if (!wasSet) {
      nextReactions[emoji] = (nextReactions[emoji] || 0) + 1;
      nextMine = [emoji];
    }
    onReacted?.(item.project_id, nextReactions, nextMine);
    try {
      await axios.post(
        `${API}/doserne/progress/project/${item.project_id}/react`,
        { emoji },
        authHeader(),
      );
    } catch (e) {
      // Roll back optimistic update.
      onReacted?.(item.project_id, item.reactions || {}, item.my_reactions || []);
      toast.error(e?.response?.data?.detail || "Reaksjon feilet");
    }
    setReacting(false);
  };

  const deleteAll = async () => {
    if (!window.confirm(`Slette dette innlegget for ${item.project_name || "prosjektet"}? Dette kan ikke angres.`)) return;
    setDeleting(true);
    try {
      // Feed is per-post now, so use the per-post delete endpoint.
      // `item.post_id` is the id of the single progress post; falling
      // back to project_id preserves the legacy "delete-all-per-project"
      // behaviour if the backend ever grouped them again.
      const postId = item.post_id || item.project_id;
      await axios.delete(`${API}/doserne/progress/${postId}`, authHeader());
      toast.success("Innlegget ble slettet");
      onRemoved?.(item.project_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke slette");
    }
    setDeleting(false);
  };

  return (
    <article className="bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition p-4 sm:p-5" data-testid={`feed-item-${item.project_id}`}>
      <header className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center text-sm font-black flex-shrink-0" style={{ color: "#fff" }}>
            {initials || <Camera className="w-4 h-4" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Link
                to={`/doserne/prosjekter/${realProjectId}`}
                className="text-sm font-black text-slate-900 hover:text-emerald-700 truncate"
              >
                {item.project_name || "Prosjekt"}
              </Link>
              <MapPin className="w-3 h-3 text-slate-400 flex-shrink-0" />
            </div>
            <div className="text-[11px] text-slate-500 truncate">
              {item.latest_author_name || "Doserne"}
              {item.author_count > 1 && (
                <span className="text-slate-400"> · {item.author_count} bidragsytere</span>
              )}
              <span className="text-slate-400"> · {item.post_count} innlegg</span>
            </div>
          </div>
        </div>
        <div className="text-[11px] text-slate-400 inline-flex items-center gap-1 flex-shrink-0">
          <Clock className="w-3 h-3" /> {timeAgo(item.latest_at)}
        </div>
      </header>

      {item.latest_note && (
        <p className="text-sm text-slate-700 mb-3 line-clamp-2 whitespace-pre-wrap">{item.latest_note}</p>
      )}

      {flatPhotos.length > 0 && (
        <div className="space-y-3">
          {days.map((day) => {
            const offset = flatPhotos.findIndex((p) => (p.post_id ? p._day === day.date : true) && day.photos[0] && p.url === day.photos[0].url);
            return (
              <div key={day.date || "unknown"} data-testid={`feed-day-${item.project_id}-${day.date || "unknown"}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[11px] uppercase tracking-widest font-black text-slate-500">{day.label}</span>
                  <span className="text-[10px] text-slate-400">· {day.photos.length} bilde{day.photos.length === 1 ? "" : "r"}</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
                <PhotoMosaic
                  photos={day.photos}
                  onOpen={(i) => setLightboxIdx(offset >= 0 ? offset + i : i)}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Reactions summary — Facebook-style */}
      {Object.keys(item.reactions || {}).length > 0 && (
        <div className="mt-2 flex items-center gap-1.5" data-testid={`feed-reactions-summary-${item.project_id}`}>
          <div className="flex -space-x-1">
            {Object.entries(item.reactions).map(([em, c]) => c > 0 && (
              <span key={em} className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white border-2 border-white shadow-sm text-sm" title={`${c}`}>{em}</span>
            ))}
          </div>
          <span className="text-[11px] text-slate-500">
            {Object.values(item.reactions).reduce((a, b) => a + b, 0)} reaksjoner
          </span>
        </div>
      )}

      <div className="mt-2 flex items-center gap-1">
        <button
          onClick={() => react("👍")}
          disabled={reacting}
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-black transition ${
            (item.my_reactions || []).includes("👍")
              ? "bg-[#0084ff] text-white shadow-sm"
              : "bg-slate-100 hover:bg-slate-200 text-slate-600"
          }`}
          style={(item.my_reactions || []).includes("👍") ? { color: "#fff" } : {}}
          data-testid={`feed-react-thumb-${item.project_id}`}
          title="Liker!"
        >
          <ThumbsUp className="w-3.5 h-3.5" /> Liker
        </button>
        <button
          onClick={() => react("❤️")}
          disabled={reacting}
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-black transition ${
            (item.my_reactions || []).includes("❤️")
              ? "bg-red-500 text-white shadow-sm"
              : "bg-slate-100 hover:bg-slate-200 text-slate-600"
          }`}
          style={(item.my_reactions || []).includes("❤️") ? { color: "#fff" } : {}}
          data-testid={`feed-react-heart-${item.project_id}`}
          title="Elsker det!"
        >
          <Heart className={`w-3.5 h-3.5 ${(item.my_reactions || []).includes("❤️") ? "fill-current" : ""}`} /> Elsker
        </button>
        <button
          onClick={() => react("🔥")}
          disabled={reacting}
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-black transition ${
            (item.my_reactions || []).includes("🔥")
              ? "bg-orange-500 text-white shadow-sm"
              : "bg-slate-100 hover:bg-slate-200 text-slate-600"
          }`}
          style={(item.my_reactions || []).includes("🔥") ? { color: "#fff" } : {}}
          data-testid={`feed-react-fire-${item.project_id}`}
          title="Fyr!"
        >
          🔥 Fyr
        </button>
        <button
          onClick={() => react("👏")}
          disabled={reacting}
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-black transition ${
            (item.my_reactions || []).includes("👏")
              ? "bg-amber-500 text-white shadow-sm"
              : "bg-slate-100 hover:bg-slate-200 text-slate-600"
          }`}
          style={(item.my_reactions || []).includes("👏") ? { color: "#fff" } : {}}
          data-testid={`feed-react-clap-${item.project_id}`}
          title="Bravo!"
        >
          👏 Bravo
        </button>
      </div>

      <footer className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2">
        <button
          onClick={openChat}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0084ff] hover:bg-[#0074e1] text-xs font-black"
          style={{ color: "#fff" }}
          data-testid={`feed-chat-${item.project_id}`}
          title="Åpne prosjektchat"
        >
          <MessageCircle className="w-3.5 h-3.5" /> Chat
        </button>
        <Link
          to={`/doserne/progresjon?project=${realProjectId}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-black"
          data-testid={`feed-timeline-${item.project_id}`}
        >
          <Camera className="w-3.5 h-3.5" /> Se alle bilder
        </Link>
        {item.can_delete_all && (
          <button
            onClick={deleteAll}
            disabled={deleting}
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 text-xs font-black disabled:opacity-50"
            data-testid={`feed-delete-${item.project_id}`}
            title="Slett alle innlegg for dette prosjektet"
          >
            {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            Slett innlegg
          </button>
        )}
      </footer>

      {lightboxIdx !== null && (
        <Lightbox
          photos={flatPhotos}
          index={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
          onPrev={() => setLightboxIdx((i) => (i - 1 + flatPhotos.length) % flatPhotos.length)}
          onNext={() => setLightboxIdx((i) => (i + 1) % flatPhotos.length)}
          canDeleteAt={canDeletePhotoAt}
          onDelete={async (idx) => {
            const target = flatPhotos[idx];
            if (!target?.url || !target?.post_id) return;
            if (!window.confirm("Slette dette bildet? Dette kan ikke angres.")) return;
            try {
              await axios.post(
                `${API}/doserne/progress/${target.post_id}/delete-photo`,
                { url: target.url },
                authHeader(),
              );
              toast.success("Bilde slettet");
              onPhotoRemoved?.(item.project_id, target.url);
              // Adjust lightbox index (if last photo, close)
              if (flatPhotos.length <= 1) setLightboxIdx(null);
              else setLightboxIdx((i) => Math.min(i, flatPhotos.length - 2));
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Kunne ikke slette bildet");
            }
          }}
        />
      )}
    </article>
  );
}

export default function ProgressFeed({ authHeader, t, lang: _lang }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/doserne/progress/feed?projects_limit=10&photos_per_project=8`,
        authHeader(),
      );
      setItems(data.items || []);
    } catch { setItems([]); }
    setLoading(false);
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const title = t?.("home.feed_title") || "Fra jobben — sist postet";

  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold inline-flex items-center gap-1.5">
          <Camera className="w-3.5 h-3.5 text-emerald-600" /> {title}
        </div>
        <Link
          to="/doserne/progresjon"
          className="text-xs text-emerald-700 hover:text-emerald-900 font-semibold inline-flex items-center gap-1"
          data-testid="feed-see-all"
        >
          {t?.("home.feed_see_all") || "Se alle bilder"}
        </Link>
      </div>

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-8 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-emerald-200 rounded-2xl p-8 text-center">
          <Camera className="w-8 h-8 text-emerald-300 mx-auto mb-2" />
          <div className="text-sm text-slate-500">Ingen bilder postet ennå.</div>
          <Link to="/doserne/progresjon" className="mt-2 inline-block text-xs text-emerald-700 hover:text-emerald-900 font-semibold">
            Legg til det første →
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((it) => (
            <FeedItem
              key={it.project_id}
              item={it}
              authHeader={authHeader}
              onRemoved={(pid) => setItems((cur) => cur.filter((x) => x.project_id !== pid))}
              onReacted={(pid, reactions, mine) => setItems((cur) => cur.map((x) => x.project_id === pid ? { ...x, reactions, my_reactions: mine } : x))}
              onPhotoRemoved={(pid, url) => setItems((cur) => cur.map((x) => x.project_id === pid ? { ...x, photos: (x.photos || []).filter((p) => p.url !== url) } : x))}
            />
          ))}
        </div>
      )}
    </section>
  );
}
